import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  ImageType,
  Text,
  UnstyledLinkLinaria,
  Usp,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {Ref} from 'react';

interface USPsItemProps {
  badge?: React.ReactNode;
  children?: React.ReactNode;
  ['data-testid']?: string;
  href?: string;
  image: ImageType;
  shouldShowAdditionalInfo?: boolean;
  text: string;
}

export const UspItem = ({
  badge,
  children,
  'data-testid': dataTestId,
  href,
  image,
  text,
  ref,
}: React.PropsWithChildren<USPsItemProps> & {ref?: Ref<HTMLElement>}) => {
  return (
    <ConditionalWrapper data-testid={dataTestId} href={href} wrapperRef={ref}>
      <Content>
        <StyledUsp image={image} text={text} />
        {badge}
      </Content>
      {children}
    </ConditionalWrapper>
  );
};

const ConditionalWrapper: React.FC<
  React.PropsWithChildren<
    Partial<USPsItemProps> & {wrapperRef?: React.Ref<HTMLElement>}
  >
> = ({href, 'data-testid': dataTestId, wrapperRef, children}) => {
  if (href) {
    return (
      <StyledLink
        to={href}
        data-testid={dataTestId}
        ref={wrapperRef as Ref<HTMLAnchorElement>}
        aria-label={undefined}
      >
        {children}
      </StyledLink>
    );
  }

  return (
    <Wrapper data-testid={dataTestId} ref={wrapperRef as Ref<HTMLDivElement>}>
      {children}
    </Wrapper>
  );
};

const wrapperStyles = `
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 16px;
  box-sizing: border-box;

  &:not(:last-child) {
    border-bottom: 1px solid ${COLORS.Grey3};
  }
`;

const StyledLink = styled(UnstyledLinkLinaria)`
  ${wrapperStyles}
`;

const Wrapper = styled.div`
  ${wrapperStyles}
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const StyledUsp = styled(Usp)`
  &&& {
    flex: 1;
  }
`;

export const Badge = styled(Text)<{$backgroundColor: string}>`
  &&& {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-inline: 10px;
    height: 19px;
    background-color: ${props => props.$backgroundColor};
    border-radius: 50px;
    margin-left: 10px;
    font-size: ${FONT_SIZES.px10};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
    color: ${COLORS.White};
  }
`;
